import React from "react";
import { withTranslation, Trans } from "react-i18next";
import { compose, withHandlers, withPropsOnChange } from "recompose";
import { Link } from "react-router-dom";
import { Control, actions, track } from "react-redux-form";
import { sumBy } from "lodash";
import { NotificationManager } from "react-notifications";
import {
  Page,
  withLocalForm,
  FormGroup,
  SubmitButton,
  onMount,
  withReload,
} from "components/common";
import { withXeroErrors } from "components";
import { formatDate, formatCurrency } from "services/util";
import { filter, some } from "lodash";
import { isCurrency } from "services/util";

export const Allocate = ({
  state,
  recalculateTotals,
  XeroErrors,
  LocalForm,
  dispatch,
  handleSubmit,
  t,
  payable,
}) => (
  <Page
    title={t("Allocate balance on {{ Reference }}", {
      Reference: state.item.Reference,
    })}
    translate={false}
  >
    <XeroErrors />
    <LocalForm
      onSubmit={handleSubmit}
      validators={{
        remainingCredit: (v) => v >= 0,
        "invoices[].AmountToCredit": (v) => {
          if (!v) return true;
          return isCurrency(v, { min: 0.01 });
        },
        invoices: (v) => {
          return some(v, (x) => parseFloat(x.AmountToCredit) > 0);
        },
      }}
    >
      <table className="table table-sm small responsive">
        <thead>
          <tr>
            <th>
              <Trans>{payable ? "Bill" : "Invoice"}</Trans>
            </th>
            <th>
              <Trans>Date</Trans>
            </th>
            <th>
              <Trans>{payable ? "Billed" : "Invoiced"}</Trans>
            </th>
            <th>
              <Trans>Amount Due</Trans>
            </th>
            <th>
              <Trans>Amount to Credit</Trans>
            </th>
          </tr>
        </thead>
        <tbody>
          {state.invoices.map((i, idx) => {
            const model = track("local.invoices[].AmountToCredit", {
              InvoiceID: i.InvoiceID,
            });

            return (
              <tr key={i.InvoiceID}>
                <td>
                  <Link
                    to={`/accounts_receivable/invoices/edit/${i.InvoiceID}`}
                  >
                    {i.HesaabNumber || i.InvoiceNumber}
                  </Link>
                </td>
                <td>{formatDate(i.Date)}</td>
                <td>{formatCurrency(i.Total)}</td>
                <td>{formatCurrency(i.AmountDue)}</td>
                <td>
                  <FormGroup model={model}>
                    <Control.text
                      readOnly={i.__readOnly}
                      updateOn="blur"
                      changeAction={(m, v) => {
                        dispatch(actions.change(m, v));
                        recalculateTotals();
                      }}
                      type="number"
                      step="0.01"
                      model={model}
                      className="form-control-sm"
                    />
                  </FormGroup>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="d-flex justify-content-end">
        <div>
          <table className="table borderless">
            <tbody>
              <tr>
                <td>
                  <Trans>Outstanding Credit Balance</Trans>
                </td>
                <td>{formatCurrency(state.item.RemainingCredit)}</td>
              </tr>
              <tr>
                <td>
                  <Trans>Total Amount to Credit</Trans>
                </td>
                <td>{formatCurrency(state.totalAmountToCredit)}</td>
              </tr>
              <tr className="border-top border-dark font-weight-bold">
                <td>
                  <Trans>Remaining Credit</Trans>
                </td>
                <td>{formatCurrency(state.remainingCredit)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="d-flex justify-content-end">
        <SubmitButton>Allocate Credit</SubmitButton>
      </div>
    </LocalForm>
  </Page>
);

export const enhance = compose(
  withReload(),
  withXeroErrors(),
  onMount(
    ({ xero, match, setXeroErrors, payable }) =>
      () =>{
        const {docType, id, transactionType} = match.params
        const payable = transactionType === "spend"
        return xero
        .get(`${docType}/${id}`)
        .get("data")
        .then(
          (item) =>
            Promise.props({
              item: item,
              invoices: xero
                .get("invoices", {
                  params: {
                    extra_params: {
                      ContactIDs: item.Contact.ContactID,
                      Statuses: "AUTHORISED",
                    },
                    order: "Date",
                    Type: payable ? "ACCPAY" : "ACCREC",
                    raw: `Type="${payable ? "ACCPAY" : "ACCREC"}"`,
                  },
                })
                .get("data"),
            }).then(
              ({ invoices, item }) => ({
                data: {
                  invoices: filter(
                    invoices,
                    (i) => i.CurrencyCode === item.CurrencyCode
                  ),
                  item,
                  remainingCredit: item.RemainingCredit,
                  totalAmountToCredit: 0,
                },
              }),
              setXeroErrors
            ),
          setXeroErrors
        )
      }
        
  ),
  withLocalForm({
    initialState: ({ data }) => data,
  }),
  withTranslation(),
  withHandlers({
    recalculateTotals:
      ({ dispatch, state }) =>
      () => {
        dispatch((dispatch, getState) => {
          const { local } = getState();
          const totalAmountToCredit = sumBy(
            local.invoices,
            (v) => parseFloat(v.AmountToCredit) || 0
          );
          const remainingCredit =
            local.item.RemainingCredit - totalAmountToCredit;
          dispatch(
            actions.change("local.totalAmountToCredit", totalAmountToCredit)
          );
          dispatch(actions.change("local.remainingCredit", remainingCredit));
        });
      },
  }),
  withHandlers({
    handleSubmit:
      ({ xero, reload, t, match, dispatch, setXeroErrors, recalculateTotals, history}) =>
      (state) => {
        const allocations = filter(
          state.invoices,
          (v) => parseFloat(v.AmountToCredit) > 0
        );
        const {docType, id} = match.params
        const id_name = docType === "prepayments" ? "PrepaymentID" : "OverpaymentID"
        Promise.mapSeries(allocations, (v, idx) =>
          xero
            .post(`${docType}/${state.item[id_name]}/allocations`, [
              {
                AppliedAmount: v.AmountToCredit,
                Invoice: {
                  InvoiceID: v.InvoiceID,
                },
              },
            ])
            .tap(() => {
              dispatch((dispatch, getState) => {
                const state = getState();
                const {
                  local: { item },
                } = state;
                const model = track("local.invoices[]", {
                  InvoiceID: v.InvoiceID,
                })(state);
                dispatch(
                  actions.change(
                    "local.item.RemainingCredit",
                    item.RemainingCredit - parseFloat(v.AmountToCredit)
                  )
                );
                dispatch(actions.change(`${model}.AmountToCredit`, ""));
                dispatch(actions.change(`${model}.__readOnly`, true));
              });
            })
        )
          .then(
            () => {
              NotificationManager.success(t("Credit successfully applied"));
              history.push(history.resolvePath(".."));
            },
            (err) => {
              recalculateTotals();
              setXeroErrors(err);
            }
          )
          .finally(() => {
            dispatch(actions.setPending("local", false));
          });
      },
  }),
  withPropsOnChange([], ({ recalculateTotals }) => recalculateTotals())
);

export default enhance(Allocate);
